import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Building } from "lucide-react";
import PageLayout from "@/components/PageLayout";

const SUBJECTS = ["General Inquiry", "Sales", "Partnership", "Support", "Press", "Other"];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", company: "", subject: "General Inquiry", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSending(true);
    setTimeout(() => {
      setSending(false);
      toast.success("Thank you! We will respond within 24 hours.");
      setForm({ name: "", email: "", company: "", subject: "General Inquiry", message: "" });
    }, 800);
  };

  return (
    <PageLayout>
      <div className="max-w-5xl mx-auto px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="contact-heading">Contact Us</h1>
          <p className="text-lg text-[var(--qn-foreground-muted)] mb-12">We would love to hear from you. Reach out for inquiries, partnerships, or support.</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <div className="flex items-center gap-2 mb-1"><Mail className="w-4 h-4 text-[var(--qn-primary)]" /><h3 className="font-semibold text-sm">General Inquiries</h3></div>
              <a href="mailto:contact@qnneal.dev" className="text-sm text-[var(--qn-foreground-muted)] hover:text-[var(--qn-primary)]">contact@qnneal.dev</a>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1"><Mail className="w-4 h-4 text-[var(--qn-primary)]" /><h3 className="font-semibold text-sm">Sales & Partnerships</h3></div>
              <a href="mailto:sales@qnneal.dev" className="text-sm text-[var(--qn-foreground-muted)] hover:text-[var(--qn-primary)]">sales@qnneal.dev</a>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1"><Phone className="w-4 h-4 text-[var(--qn-primary)]" /><h3 className="font-semibold text-sm">Phone</h3></div>
              <p className="text-sm text-[var(--qn-foreground-muted)]">+47 404 00 755</p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1"><MapPin className="w-4 h-4 text-[var(--qn-primary)]" /><h3 className="font-semibold text-sm">Headquarters</h3></div>
              <p className="text-sm text-[var(--qn-foreground-muted)] leading-relaxed">Global InTech AS<br />R&oslash;o 95<br />5457 H&oslash;ylandsbygd<br />Norway</p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1"><Building className="w-4 h-4 text-[var(--qn-primary)]" /><h3 className="font-semibold text-sm">Organisasjonsnummer</h3></div>
              <p className="text-sm text-[var(--qn-foreground-muted)]">NO 935 706 998</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="lg:col-span-3 space-y-5" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Full Name *</label>
                <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)] text-sm focus:outline-none focus:ring-1 focus:ring-[var(--qn-primary)]" data-testid="contact-name" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Email *</label>
                <input required type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)] text-sm focus:outline-none focus:ring-1 focus:ring-[var(--qn-primary)]" data-testid="contact-email" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Company <span className="text-[var(--qn-foreground-subtle)]">(optional)</span></label>
              <input value={form.company} onChange={e => setForm({...form, company: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)] text-sm focus:outline-none focus:ring-1 focus:ring-[var(--qn-primary)]" data-testid="contact-company" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Subject</label>
              <select value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)] text-sm focus:outline-none focus:ring-1 focus:ring-[var(--qn-primary)]" data-testid="contact-subject">
                {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Message *</label>
              <textarea required rows={5} value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)] text-sm focus:outline-none focus:ring-1 focus:ring-[var(--qn-primary)] resize-none" data-testid="contact-message" />
            </div>
            <Button type="submit" className="btn-primary w-full sm:w-auto" disabled={sending} data-testid="contact-submit">
              {sending ? "Sending..." : "Send Message"}
            </Button>
          </form>
        </div>
      </div>
    </PageLayout>
  );
}
